import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

async function buildProfile(authUser, existingSession, setUser) {
  let role = null;
  try {
    if (existingSession) {
      const rpc = supabase.rpc('get_my_ecosystem_role');
      const timeout = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'rpc_timeout' } }), 5000));
      const { data, error } = await Promise.race([rpc, timeout]);
      if (error) {
        role = null;
        console.warn('get_my_ecosystem_role timeout/erreur:', error);
        // Late resolve: if RPC finishes after timeout, update role if still null
        if (setUser) {
          rpc.then(({ data: lateData }) => {
            if (lateData && typeof lateData === 'string') {
              setUser(prev => (prev && !prev.role) ? { ...prev, role: lateData } : prev);
            }
          }).catch(() => {});
        }
      } else {
        role = data || null;
      }
    }
  } catch (err) {
    console.error('buildProfile RPC failed:', err);
  }
  return {
    id: authUser.id,
    email: authUser.email,
    name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || authUser.email,
    role,
    assigned_club_ids: [],
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const getSessionWithTimeout = Promise.race([
      supabase.auth.getSession(),
      new Promise((resolve) => setTimeout(() => resolve({ data: { session: null } }), 5000))
    ]);
    getSessionWithTimeout.then(async ({ data: { session: s } }) => {
      if (cancelled) return;
      if (s) {
        setSession(s);
        setUser(await buildProfile(s.user, s, setUser));
      }
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    // Safety: never stay on Loading more than 3s
    const safety = setTimeout(() => { if (!cancelled) setLoading(false); }, 3000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (event === 'SIGNED_IN' && s) {
          setSession(s);
          setUser(await buildProfile(s.user, s, setUser));
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
        } else if (event === 'TOKEN_REFRESHED' && s) {
          setSession(s);
        }
      }
    );

    return () => { cancelled = true; clearTimeout(safety); subscription.unsubscribe(); };
  }, []);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${session?.access_token}` }
  }), [session]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setSession(data.session);
    const profile = await buildProfile(data.user, data.session, setUser);
    setUser(profile);
    return profile;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      login,
      logout,
      authHeaders,
      isAdmin: ['admin', 'super_admin'].includes(user?.role),
      supabase,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
